import { PrintTrimData, TrimConfigEntity } from '../../model/trim.print-model';
import { TrimPrintManager } from '../../print/trim.print';
import PixelConverter, { WH } from '../../../utilities/pixel-converter';
import { TrimDataManager } from './trim-data.manager';
import { UuidGenerator } from '../../../utilities/uuid-generator';
import { CJ_CONTAINER_NAME } from '../../model/cj-factory/trim.cj-obj-name.collection';
import { ColorCodeConverter } from '../../../utilities/colorcode-converter';
import { TrimEditableImageManager } from '../editable-image/trim.editable-image.manager';
import { TrimImageEditManager } from '../image-edit/trim.image-edit.manager';
import { ModeInOutType } from '../../../components/pages/sift/print-trim/hooks/use-print-trim';
import { TextFontStyle, TrimTextEvent } from '../image-edit/text/trim.text-event';
import { ImageEditManager } from '../../../layout-editor/manager/image-edit/image-edit.manager';
import { TextFont } from '../../../components/pages/layout/toolbar/layout.free-text';
import { TrimCjTool } from '../../../utilities/trim.cj-tool';

export type TextImageData = {
  textData: File[],
  deleteData: string[],
}

export class TrimDataFormatter {

  constructor(
    private canvasData: PrintTrimData,
    private printMng: TrimPrintManager,
    private editableImgMng: TrimEditableImageManager,
    private imageEditManager: TrimImageEditManager,
    private dataMng: TrimDataManager,
  ) {}
  // Xmlへの保存処理
  canvas2LaboOrder(): PrintTrimData {
    this.setViewData();
    this.setImageData();
    this.setLayoutFreeGraphics();
    this.setLayoutTexts();
    this.setConfig();
    const laboOrderData = this.canvasData;
    return laboOrderData;
  }

  private setViewData() {
    const photo = this.dataMng.getPhotoContainer();
    const pos = {x: photo.defaultPos.x - photo.x, y: photo.defaultPos.y - photo.y};
    const scale = photo.scaleX;
    const rotate = photo.rotation;
    const id = photo.editableImageId ?? UuidGenerator.create();
    const fileName = photo.fileName;
    this.canvasData.setImageTrimViewData({ id, fileName, pos, scale, rotate })

    this.canvasData.view.stamps.length = 0;
    this.dataMng.getFreeGraphics().forEach(v => {
      if (v.editableImageId) {
        const editableImage = this.editableImgMng.list.find(editableImg => editableImg.id === v.editableImageId);
        if (!editableImage) return;
        this.canvasData.addStampView({
          id: v.editableImageId,
          fileName: v.fileName,
          pos: {x: v.x, y: v.y},
          scale: v.scaleX,
          rotate: v.rotation,
        })
      }
    })

    this.canvasData.view.texts.length = 0;
    this.dataMng.getTexts().filter(v => v.name !== CJ_CONTAINER_NAME.dateText).forEach(v => {
      if (v.textId) {
        this.canvasData.addTextView({
          id: v.textId,
          fileName: '',
          pos: {x: v.x, y: v.y},
          scale: v.scaleX,
          rotate: v.rotation,
        })
      }
    })
  }

  private setImageData() {
    const editableImage = this.printMng.editableImage;
    if (!editableImage) { return this.canvasData }

    const photo = this.dataMng.getPhotoContainer();
    const scale = photo.scaleX;
    const photoData = photo.getBounds();
    const base = this.canvasData.temp.base;
    const frame = this.canvasData.image.frame || { ...this.canvasData.temp.base, x: 0, y: 0 };
    const tempSize: WH = {width: PixelConverter.mmToPx(base.width - (frame.x * 2)) / TrimDataManager.getAdditionalValue, height: PixelConverter.mmToPx(base.height - (frame.y * 2)) / TrimDataManager.getAdditionalValue};
    const rotate = photo.rotation * 100;
    const z = PixelConverter.mmToPx(base.width) / Number(editableImage.virtualWidth);
    const theta = (rotate/100 * Math.PI) / 180;
    // 差分ベクトル（中心との差）
    const cx = tempSize.width / 2;
    const cy = tempSize.height / 2;
    const x = photo.x;
    const y = photo.y;
    const dx = x - cx;
    const dy = y - cy;
    // 逆回転させて 0度の位置を求める
    const originalX = cx + dx * Math.cos(-theta) - dy * Math.sin(-theta);
    const originalY = cy + dx * Math.sin(-theta) + dy * Math.cos(-theta);
    // もとの位置との差分（動いた分）
    const movePos = {
      x: originalX - photo.defaultPos.x + (tempSize.width - photoData.width * scale) / 2,
      y: originalY - photo.defaultPos.y + (tempSize.height - photoData.height * scale) / 2,
    }

    this.canvasData.setImageTrimData(tempSize, z, movePos, rotate, photo.scaleX);
  }

  private setLayoutFreeGraphics() {
    this.canvasData.stamps.length = 0;
    this.dataMng.getFreeGraphics().forEach(v => {
      if (v.editableImageId) {
        const editableImage = this.editableImgMng.list.find(editableImg => editableImg.id === v.editableImageId);
        if (!editableImage) return;
        this.canvasData.addStamp({
          id: v.editableImageId,
          depth: v.depth,
          fileName: v.virtualPath ? v.virtualPath : editableImage.path,
          x: PixelConverter.pxToMm(v.x),
          y: PixelConverter.pxToMm(v.y),
          width: PixelConverter.pxToMm(v.real.width * v.scaleX),
          height: PixelConverter.pxToMm(v.real.height * v.scaleX),
          rotate: v.rotation * 100,
          scale: v.scaleX,
        })
      }
    })
  }

  private setLayoutTexts() {
    this.canvasData.texts.length = 0;
    if (this.imageEditManager.stage) {
      const dateText = TrimCjTool.getDateTextContainer(this.imageEditManager.stage);
      const isDate = !!dateText?.visible;
      if (!isDate && this.canvasData.temp.dateText) {
        this.canvasData.temp.dateText.value = '';
      }
    }
    this.dataMng.getTexts().filter(v => v.name !== CJ_CONTAINER_NAME.dateText).forEach((v, i) => {
      if (v.textId) {
        const font: TextFontStyle = {
          isBold: v.bold ?? '',
          isItalic: v.italic ?? '',
          family: v.fontName ?? 'ＭＳ ゴシック',
          size: v.textSize ?? '50px',
          outline: 0,
        }
        const data = v.getTransformedBounds();
        // const data = TrimTextEvent.measureActualGlyphBox(v.getTransformedBounds(), font, v.text);
        this.canvasData.addText({
          id: v.textId,
          depth: (this.printMng.currentPrint?.base.window.length ?? 0) + i + 1,
          x: PixelConverter.pxToMm(v.x * TrimDataManager.getAdditionalValue),
          y: PixelConverter.pxToMm(v.y * TrimDataManager.getAdditionalValue),
          width: PixelConverter.pxToMm(data.width * TrimDataManager.getAdditionalValue),
          height: PixelConverter.pxToMm(data.height * TrimDataManager.getAdditionalValue),
          value: v.text ?? '',
          point: PixelConverter.pxToPt(Number((v.textSize ?? '9px').replace('px', ''))) * TrimDataManager.getAdditionalValue * 10 || 90,
          font: v.fontName ?? 'ＭＳ Ｐゴシック',
          style: {
            bold: !!v.bold,
            italic: !!v.italic,
            outline: {width: 0, color: '#000000'},
            color: ColorCodeConverter.Color2Hex(v.fontColor ?? '#000000'),
          },
          alignment: v.horizontalAlignment ?? '0',
          rotate: v.rotation * 100,
          scale: v.scaleX,
        })
      }
    })
  }

  private setConfig() {
    const { isAllowMargin, modeInOut } = this.imageEditManager.printMenu;
    const getTrimBaseType = (mode: ModeInOutType) => {
      switch (mode) {
        case 'out':
          return 1
        case 'in':
          return 2
        case 'eq':
          return 3
      }
    }
    const config: TrimConfigEntity = {
      space: isAllowMargin ? 1 : 0,
      trimBaseType: getTrimBaseType(modeInOut),
    }
    this.canvasData.config = config;
  }

}
